const INTERNAL_PREFIX = 'internal_';
const CLASS_SETUP_METHODS = new Set(['finishSetup']);
const GENERIC_CRUD_METHODS = new Set(['fetch', 'save', 'delete', 'deleteAll', 'reset']);

function isActionMethod(key: string, value: unknown): value is (...args: unknown[]) => unknown {
  return (
    key !== 'constructor' &&
    typeof value === 'function' &&
    !key.startsWith('#') &&
    !CLASS_SETUP_METHODS.has(key) &&
    !GENERIC_CRUD_METHODS.has(key)
  );
}

function collectActionEntries(instance: object): [string, (...args: unknown[]) => unknown][] {
  const entries = new Map<string, (...args: unknown[]) => unknown>();
  const prototypes: object[] = [];
  let current: object | null = instance;

  while (current && current !== Object.prototype) {
    prototypes.push(current);
    current = Object.getPrototypeOf(current);
  }

  for (const proto of prototypes) {
    for (const key of Object.getOwnPropertyNames(proto)) {
      if (!isActionMethod(key, (proto as Record<string, unknown>)[key])) {
        continue;
      }
      if (key.startsWith(INTERNAL_PREFIX)) {
        continue;
      }
      const method = (proto as Record<string, unknown>)[key] as (...args: unknown[]) => unknown;
      entries.set(key, method.bind(instance));
    }
  }

  for (const key of Object.getOwnPropertyNames(instance)) {
    const value = (instance as Record<string, unknown>)[key];
    if (!isActionMethod(key, value)) {
      continue;
    }
    if (key.startsWith(INTERNAL_PREFIX)) {
      continue;
    }
    entries.set(key, value.bind(instance));
  }

  return [...entries.entries()];
}

export function flattenActions<T extends object>(instances: object[]): T {
  return instances.reduce<T>((acc, instance) => {
    for (const [key, boundMethod] of collectActionEntries(instance)) {
      (acc as Record<string, unknown>)[key] = boundMethod;
    }
    return acc;
  }, {} as T);
}
